import { useAuth0 } from '@auth0/auth0-react';
import { CircularProgress } from '@mui/material';
import React from 'react';
import { StyledButton } from '../../components/styles/StyledButtons';
import { StyledTypography } from '../../components/styles/styles';
import { redirectTo } from '../../utils/navigation';
import type { HandoffStatus } from './useAppHandoffRedeem';

const SECONDARY_SX = { color: 'text.secondary', fontSize: '0.9rem' } as const;
const HEADING_SX = { fontWeight: 600 } as const;
const INVALID_AUTH_HEADING = 'Invalid authorisation';
const RESUME_HINT = 'you’ll come straight back here.';

function SecondaryText({ children }: { children: React.ReactNode }) {
  return (
    <StyledTypography tone="tone1" sx={SECONDARY_SX}>
      {children}
    </StyledTypography>
  );
}

/**
 * Shared shell for the two "wrong session" outcomes: a heading plus a plain
 * log-out button. We never name the admin the handoff was created for — the
 * caller only supplies generic guidance.
 *
 * `returnTo` is this same handoff URL (with its `ht` token), so after the user
 * signs back in as the right admin they land here again and the redeem retries
 * automatically — no re-scan. That is safe: the token is already in this
 * browser's address bar, it is single-use and short-lived, and the backend
 * still only mints the deep link when the redeeming admin's sub matches the
 * handoff's actor_sub.
 */
function InvalidAuthPanel({ children }: { children: React.ReactNode }) {
  const { logout } = useAuth0();
  return (
    <>
      <StyledTypography tone="tone1" sx={HEADING_SX}>
        {INVALID_AUTH_HEADING}
      </StyledTypography>
      {children}
      <StyledButton
        variant="contained"
        onClick={() => logout({ logoutParams: { returnTo: window.location.href } })}
      >
        Log out
      </StyledButton>
    </>
  );
}

function WrongSessionPanel({ kind, email }: { kind: 'mismatch' | 'notAdmin'; email?: string }) {
  return (
    <InvalidAuthPanel>
      {kind === 'mismatch' ? (
        <>
          <SecondaryText>
            {email ? `You are logged in as ${email}.` : 'You are logged in as a different account.'}{' '}
            This impersonation link was created by another admin.
          </SecondaryText>
          <SecondaryText>
            If it was meant for you, log out and sign in with the correct account — {RESUME_HINT}
          </SecondaryText>
        </>
      ) : (
        <>
          <SecondaryText>
            {email
              ? `You are logged in as ${email}, which is not an admin account.`
              : 'You are not logged in as an admin account.'}
          </SecondaryText>
          <SecondaryText>
            Log out and sign in as the admin this QR code was created for — {RESUME_HINT}
          </SecondaryText>
        </>
      )}
    </InvalidAuthPanel>
  );
}

export function ImpersonateAppHandoffStatus({ status }: { status: HandoffStatus }) {
  const { user } = useAuth0();

  if (status.kind === 'working') {
    return (
      <>
        <CircularProgress size={32} />
        <StyledTypography tone="tone1">Verifying your identity…</StyledTypography>
      </>
    );
  }

  if (status.kind === 'opening') {
    return (
      <>
        <StyledTypography tone="tone1" sx={HEADING_SX}>
          Opening the Fides app…
        </StyledTypography>
        <SecondaryText>
          If nothing happens, make sure the Fides app for this environment is installed, then tap
          below.
        </SecondaryText>
        <StyledButton variant="contained" onClick={() => redirectTo(status.mobileRedirectUrl)}>
          Open app
        </StyledButton>
      </>
    );
  }

  if (status.kind === 'mismatch' || status.kind === 'notAdmin') {
    return <WrongSessionPanel kind={status.kind} email={user?.email} />;
  }

  if (status.kind === 'invalid') {
    return (
      <SecondaryText>
        This link has expired or was already used. Generate a new one from the contact page.
      </SecondaryText>
    );
  }

  return (
    <SecondaryText>
      This link is missing its handoff token. Generate a new one from the contact page.
    </SecondaryText>
  );
}

export default ImpersonateAppHandoffStatus;
